/* eslint-disable react/prop-types */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Box, CircularProgress } from '@mui/material';
import {
  subscribeAuth,
  getCurrentUser,
  getAccessToken,
  setAccessToken,
  setCurrentUser,
  clearAuthState,
  isAuthenticated as checkAuthenticated,
  getUserRoleCode,
  logout as authLogout,
} from '../utils/auth.js';
import {
  fetchCurrentUserProfile,
  tryRefreshSession,
} from '../services/api.js';

const AuthContext = createContext(null);

/**
 * status:
 * - idle: before first bootstrap
 * - loading: bootstrap in progress
 * - authenticated: access token + user in memory
 * - anonymous: no valid session
 */
export function AuthProvider({ children }) {
  const [status, setStatus] = useState('idle');
  const [user, setUser] = useState(() => getCurrentUser());
  const [, setTick] = useState(0);

  useEffect(() => {
    return subscribeAuth(() => {
      setUser(getCurrentUser());
      setTick(t => t + 1);
    });
  }, []);

  const bootstrap = useCallback(async () => {
    setStatus('loading');
    try {
      if (!getAccessToken()) {
        const refreshed = await tryRefreshSession();
        if (!refreshed) {
          clearAuthState();
          setStatus('anonymous');
          return;
        }
      }

      const profile = await fetchCurrentUserProfile();
      if (profile) {
        setCurrentUser(profile);
        setUser(profile);
        setStatus('authenticated');
      } else {
        clearAuthState();
        setStatus('anonymous');
      }
    } catch {
      clearAuthState();
      setStatus('anonymous');
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const logout = useCallback(async () => {
    await authLogout({ redirect: true });
  }, []);

  const completeLogin = useCallback(async accessToken => {
    setAccessToken(accessToken);
    const profile = await fetchCurrentUserProfile();
    if (profile) {
      setCurrentUser(profile);
      setUser(profile);
      setStatus('authenticated');
    }
    return profile;
  }, []);

  const value = useMemo(
    () => ({
      status,
      user,
      isAuthenticated: checkAuthenticated(),
      userRole: getUserRoleCode(),
      bootstrap,
      logout,
      completeLogin,
      isBootstrapping: status === 'idle' || status === 'loading',
    }),
    [status, user, bootstrap, logout, completeLogin]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}

/** Full-page spinner while session bootstrap runs */
export function AuthBootstrapGate({ children }) {
  const { isBootstrapping } = useAuth();

  if (isBootstrapping) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return children;
}
